import { FC } from 'react'
import { ImageLoader } from 'next/legacy/image'

import { SanityImageMediaPayload, SanityImageType } from '@portfolio/models'

import { MediaProps } from '../models/MediaProps'
import { getMediaSizeByRatio } from '../helpers/getMediaSizeByRatio'
import { ResponsiveMediaComponent } from './ResponsiveMediaComponent'
import { Image } from './Image'
import { imageUrlBuilder } from '@portfolio/services/sanity-service'

type SanityImageProps = Omit<MediaProps, 'mediaPayload'> & {
  mediaPayload: SanityImageMediaPayload
}

type ImageAdapterProps = Omit<MediaProps, 'mediaPayload'> & {
  image: SanityImageType
  blur?: boolean
  unoptimized?: boolean
}

const placeholderBase64 =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNsUAMAASwAqHb28sMAAAAASUVORK5CYII='

const ImageAdapter: FC<ImageAdapterProps> = ({
  alt,
  hardcropRatio,
  objectFit,
  priority,
  sizes,
  layout,
  image,
  blur = false,
  quality,
}) => {
  if (!image?.asset) {
    return null
  }

  const buildedImage = imageUrlBuilder.image(image)

  const loader: ImageLoader = ({ width: loaderWidth, quality }) => {
    const [width, height] = getMediaSizeByRatio({
      width: loaderWidth,
      ratioType: hardcropRatio,
    })
    const resolvedQuality = quality ?? 75

    if (height && width) {
      return buildedImage
        .width(width)
        .height(height)
        .quality(resolvedQuality)
        .auto('format')
        .url()
    }

    if (width) {
      return buildedImage
        .width(width)
        .quality(resolvedQuality)
        .auto('format')
        .url()
    }

    return buildedImage.quality(resolvedQuality).auto('format').url()
  }

  const [width, height] = getMediaSizeByRatio({
    width: image.width,
    crop: image.crop,
    ratioType: hardcropRatio,
    height: image.height,
  })

  const url = (() => {
    try {
      return buildedImage.url()
    } catch {
      return undefined
    }
  })()

  if (!url) {
    return null
  }

  return (
    <Image
      alt={alt || image.alt}
      objectFit={objectFit}
      priority={priority}
      src={url}
      sizes={sizes}
      layout={layout}
      width={width}
      height={height}
      loader={loader}
      quality={quality}
      placeholder={blur ? 'blur' : 'empty'}
      blurDataURL={placeholderBase64}
      unoptimized={image.unoptimized}
    />
  )
}

export const SanityImage: FC<SanityImageProps> = ({
  mediaPayload,
  ...restOfProps
}) => {
  const { image, mobileImage } = mediaPayload

  return (
    <ResponsiveMediaComponent
      mobileComponent={
        mobileImage && <ImageAdapter image={mobileImage} {...restOfProps} />
      }
      component={<ImageAdapter image={image} {...restOfProps} />}
    />
  )
}
